import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

/**
 * A team's tags, live, newest-touched first — the hook replacement for
 * StrikeTeamTagsModel's viewModel-callback wiring (screens we touch migrate;
 * house rule 2026-08-27). Running-total fields (totalNetValue, countOfTaps)
 * ride the tag docs themselves, so one subscription carries the whole list.
 *
 * Shape: [loading, tags, error, intents { updateTag }].
 */
export function useTeamTags(teamId) {
    const [state, setState] = useState({ loading: true, tags: [], error: null });

    useEffect(() => {
        if (!teamId) { setState({ loading: false, tags: [], error: null }); return undefined; }
        setState({ loading: true, tags: [], error: null });
        return firebase.firestore()
            .collection('tags')
            .where('teamId', '==', teamId)
            .orderBy('updatedAt', 'desc')
            .onSnapshot(
                (snap) => setState({
                    loading: false,
                    tags: snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
                    error: null,
                }),
                (err) => {
                    console.warn('[useTeamTags] listen failed:', err && err.message);
                    setState({ loading: false, tags: [], error: err.message || 'Could not load tags' });
                },
            );
    }, [teamId]);

    /** Intent: partial update to one tag. updatedAt moves so the list's
     * newest-touched ordering surfaces the edit. */
    const updateTag = async (tagId, data) => firebase.firestore()
        .collection('tags').doc(tagId)
        .update({
            ...data,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

    return [state.loading, state.tags, state.error, { updateTag }];
}

/** The team doc, live: [loading, team, error]. */
export function useTeamDetails(teamId) {
    const [state, setState] = useState({ loading: true, team: null, error: null });

    useEffect(() => {
        if (!teamId) { setState({ loading: false, team: null, error: null }); return undefined; }
        setState({ loading: true, team: null, error: null });
        return firebase.firestore()
            .collection('teams').doc(teamId)
            .onSnapshot(
                (snap) => setState({
                    loading: false,
                    team: snap.exists ? { id: snap.id, ...snap.data() } : null,
                    error: null,
                }),
                (err) => {
                    console.warn('[useTeamDetails] listen failed:', err && err.message);
                    setState({ loading: false, team: null, error: err.message || 'Could not load the team' });
                },
            );
    }, [teamId]);

    return [state.loading, state.team, state.error];
}
